import React, { useState, useEffect } from 'react';
import { Invoice, Tenant, Property, Unit, Transaction } from '../types';
import { api } from '../services/api';
import { Calendar, DollarSign, User, Home, FileText } from 'lucide-react';

interface InvoiceFormProps {
  onCancel: () => void;
  onSubmit: (data: any) => void;
}

const InputGroup = ({ label, icon: Icon, children }: { label: string, icon?: any, children: React.ReactNode }) => (
  <div className="mb-5">
    <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
    <div className="relative group">
      {Icon && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none transition-colors group-focus-within:text-fly-500 z-10">
          <Icon className="h-5 w-5 text-slate-400 group-focus-within:text-fly-500" />
        </div>
      )}
      {children}
    </div>
  </div>
);

const InvoiceForm: React.FC<InvoiceFormProps> = ({ onCancel, onSubmit }) => {
  const [formData, setFormData] = useState({
    tenantId: '',
    unitId: '',
    amount: '',
    serviceCharge: '0',
    dueDate: new Date().toISOString().split('T')[0],
    monthDue: new Date().toISOString().substring(0, 7),
    status: 'Pending'
  });

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unlinkedExpenses, setUnlinkedExpenses] = useState<Transaction[]>([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [t, p, e] = await Promise.all([
          api.tenants.getAll(), 
          api.properties.getAll(),
          api.transactions.getAll()
        ]);
        setTenants(t);
        setProperties(p);
        // Only show expenses that are NOT already linked to an invoice
        setUnlinkedExpenses(e.filter(exp => !exp.invoiceId));
      } catch (e) {
        console.error("Failed to load dependency data");
      }
    };
    loadData();
  }, []);

  // Filter units based on something if needed, or just show all. 
  // Better: Allow selecting a Tenant, then auto-fill Unit if they have one assigned.
  // Or: Select Property -> Unit -> Tenant.
  // Let's go with Tenant -> Unit (since invoice is usually against a tenant).

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tenantId = e.target.value;
    setFormData(prev => ({ ...prev, tenantId }));

    if (tenantId) {
        // Find units assigned to this tenant across all properties
        const tenantUnits: Unit[] = [];
        properties.forEach(p => {
            if (p.units) {
                p.units.forEach(u => {
                   // Loose comparison for ID (string vs number)
                   if (String(u.tenantId) === String(tenantId)) {
                       // We create a new object extending Unit but with a display-friendly unitNumber
                       // We must use 'as any' or extend the type locally if propertyName isn't in Unit
                       tenantUnits.push({ ...u, unitNumber: `${p.name} - ${u.unitNumber}` });
                   }
                });
            }
        });
        
        setUnits(tenantUnits);
        
        // Auto-select if only one unit
        if (tenantUnits.length === 1) {
            setFormData(prev => ({ ...prev, unitId: String(tenantUnits[0].id), amount: String(tenantUnits[0].rent) }));
        } else {
             setFormData(prev => ({ ...prev, unitId: '', amount: '' }));
        }
    } else {
        setUnits([]);
        setFormData(prev => ({ ...prev, unitId: '', amount: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
        ...formData,
        amount: Number(formData.amount),
        serviceCharge: Number(formData.serviceCharge || 0),
        linkedExpenseIds: selectedExpenseIds
    });
  };

  const inputClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-input";
  const selectClass = "block w-full pr-11 bg-white border border-slate-300 rounded-xl py-3 text-slate-900 focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all shadow-input appearance-none";

  return (
    <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-w-2xl mx-auto max-h-[85vh] flex flex-col">
      <div className="bg-white p-5 border-b border-slate-200 flex justify-between items-center shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-800">إصدار فاتورة جديدة</h2>
          <p className="text-slate-500 text-[10px] mt-1">إصدار فاتورة لمستأجر</p>
        </div>
      </div>

      <div className="p-4 sm:p-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
        <div className="bg-white p-6 sm:p-8 rounded-2xl border border-slate-100 shadow-sm space-y-5">
            <InputGroup label="المستأجر" icon={User}>
                <select name="tenantId" value={formData.tenantId} onChange={handleTenantChange} className={selectClass} required>
                    <option value="">إختر المستأجر</option>
                    {tenants.map(t => (
                        <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
                    ))}
                </select>
            </InputGroup>

            <InputGroup label="الوحدة" icon={Home}>
                <select name="unitId" value={formData.unitId} onChange={handleChange} className={selectClass} required>
                    <option value="">إختر الوحدة</option>
                    {units.map(u => (
                        <option key={u.id} value={u.id}>{u.unitNumber} ({u.rent} KWD)</option>
                    ))}
                </select>
                {units.length === 0 && formData.tenantId && <p className="text-xs text-red-500 mt-1">هذا المستأجر غير مسكن في أي وحدة حالياً</p>}
            </InputGroup>

            <InputGroup label="المبلغ (د.ك)" icon={DollarSign}>
                <input type="number" min="0" step="0.001" name="amount" value={formData.amount} onChange={handleChange} required className={inputClass} />
            </InputGroup>

            <InputGroup label="مبلغ خدمات إضافي (د.ك)" icon={DollarSign}>
                <input type="number" min="0" step="0.001" name="serviceCharge" value={formData.serviceCharge} onChange={handleChange} className={inputClass} placeholder="0.000" />
                <p className="text-[10px] text-slate-400 mt-1">يضاف هذا المبلغ فوق قيمة الإيجار الأساسي (مثل مصاريف صيانة أو خدمات خاصة)</p>
            </InputGroup>

            <InputGroup label="تاريخ الاستحقاق" icon={Calendar}>
                <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} required className={inputClass} />
            </InputGroup>

            <InputGroup label="عن شهر" icon={FileText}>
                <input 
                    type="month" 
                    name="monthDue" 
                    value={formData.monthDue} 
                    onChange={handleChange} 
                    required 
                    className={inputClass} 
                />
            </InputGroup>
            
            <InputGroup label="الحالة" icon={FileText}>
                 <select name="status" value={formData.status} onChange={handleChange} className={selectClass} required>
                    <option value="Pending">غير مدفوعة</option>
                    <option value="Paid">مدفوعة</option>
                </select>
            </InputGroup>
        </div>
      </div>

      <div className="p-4 sm:p-5 bg-white border-t border-slate-200 flex flex-wrap sm:flex-nowrap justify-center gap-3 sm:gap-4 shrink-0">
        <button type="button" onClick={onCancel} className="w-full sm:w-auto px-8 py-3 bg-white border border-slate-300 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-all text-sm active:scale-95">إلغاء</button>
        <button type="submit" className="w-full sm:w-auto px-10 py-3 bg-fly-600 text-white font-bold rounded-xl hover:bg-fly-700 shadow-lg shadow-fly-600/20 transition-all text-sm active:scale-95">حفظ البيانات</button>
      </div>
    </form>
  );
};

export default InvoiceForm;
